/**
 * Heurísticas para saber se uma URL de imagem tende a ser aceita pelo WhatsApp (JPEG/PNG).
 * Não substitui o servidor: URLs sem metadados continuam indeterminadas (conservador = não aceito).
 */

const ACCEPT_EXT = new Set(["jpg", "jpeg", "png", "jfif"]);

/** Extensões claramente não suportadas como imagem “padrão” no WhatsApp Business. */
const REJECT_EXT = new Set([
  "webp",
  "gif",
  "svg",
  "svgz",
  "avif",
  "heic",
  "heif",
  "bmp",
  "tif",
  "tiff",
  "ico",
]);

function normalizeMimeToken(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .split(";")[0]
    .trim();
}

function isAcceptedDataImageUrl(url) {
  const raw = String(url || "").trim();
  if (!raw.toLowerCase().startsWith("data:")) return false;
  const semi = raw.indexOf(";");
  const comma = raw.indexOf(",");
  const end = semi !== -1 ? semi : comma !== -1 ? comma : raw.length;
  const mime = normalizeMimeToken(raw.slice(5, end));
  return mime === "image/jpeg" || mime === "image/jpg" || mime === "image/png";
}

function parseUrlParts(raw) {
  const s = String(raw || "").trim();
  if (!s || s.toLowerCase().startsWith("data:")) return { pathname: "", searchParams: null };
  try {
    const u = new URL(s, "https://placeholder.local");
    return { pathname: u.pathname || "", searchParams: u.searchParams };
  } catch {
    const noHash = s.split("#")[0];
    const q = noHash.indexOf("?");
    const pathPart = (q === -1 ? noHash : noHash.slice(0, q)).trim();
    let searchParams = null;
    if (q !== -1) {
      try {
        searchParams = new URLSearchParams(noHash.slice(q + 1));
      } catch {
        searchParams = null;
      }
    }
    return { pathname: pathPart, searchParams };
  }
}

function extensionFromFilename(filename) {
  const base = String(filename || "").split("?")[0].split("#")[0];
  const lower = base.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot <= 0 || dot === lower.length - 1) return "";
  return lower.slice(dot + 1);
}

/**
 * Varre segmentos do path (da direita para a esquerda) para achar arquivo com extensão.
 * Cobre paths tipo /transform/w_400/image.jpg e falhas quando o último segmento não tem extensão.
 */
function classifyPathExtensions(pathname) {
  const segments = String(pathname || "")
    .split("/")
    .map((x) => x.split("?")[0])
    .filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    const ext = extensionFromFilename(segments[i]);
    if (!ext) continue;
    if (REJECT_EXT.has(ext)) return { verdict: "reject", ext };
    if (ACCEPT_EXT.has(ext)) return { verdict: "accept", ext };
  }
  return { verdict: "unknown", ext: "" };
}

function mimeFromQuery(searchParams) {
  if (!searchParams) return null;
  const keys = ["format", "fmt", "fm", "type", "contenttype", "response-content-type", "ct"];
  for (const k of keys) {
    const raw = searchParams.get(k);
    if (raw == null) continue;
    const v = normalizeMimeToken(raw);
    if (!v) continue;
    if (v === "jpg" || v === "jpeg" || v === "pjpeg" || v === "image/jpeg" || v === "image/jpg") {
      return "jpeg";
    }
    if (v === "png" || v === "image/png" || v === "x-png") return "png";
  }
  return null;
}

/** Cloudinary (e similares) embutem o formato no path: /image/upload/f_jpg,q_auto/... */
function formatHintFromCdnPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  if (/\/f_jpe?g([,/]|$)/.test(p)) return "jpeg";
  if (/\/f_png([,/]|$)/.test(p)) return "png";
  return null;
}

/**
 * Retorna true se a URL indica JPEG ou PNG de forma confiável (extensão, data URL ou hint em query).
 */
export function isWhatsappAcceptedImageUrl(url) {
  if (!url || !String(url).trim()) return false;
  if (isAcceptedDataImageUrl(url)) return true;

  const { pathname, searchParams } = parseUrlParts(url);
  const qMime = mimeFromQuery(searchParams);
  if (qMime === "jpeg" || qMime === "png") return true;

  const pathKind = classifyPathExtensions(pathname);
  if (pathKind.verdict === "reject") return false;
  if (pathKind.verdict === "accept") return true;

  const cdnHint = formatHintFromCdnPath(pathname);
  if (cdnHint === "jpeg" || cdnHint === "png") return true;

  return false;
}
